import { Link } from "react-router-dom";
import {
    Calculator, ShoppingBag, Rocket, Briefcase, Building, Stethoscope, Cpu, Truck, ArrowUpRight,
} from "lucide-react";
import IndustriesAccordion from "@/components/IndustriesAccordion";
import Seo from "@/components/Seo";

const INDUSTRIES = [
    {
        id: "accounting",
        icon: Calculator,
        title: "Accounting & Financial Services",
        challenges: "Managing high-volume data, rigorous compliance deadlines and seasonal workload volatility.",
        solutions: "Audit-ready bookkeeping and tax preparation, scalable buffer capacity that protects data integrity during your busiest fiscal peaks.",
        advantage: "We manage recurring financial audits annually, ensuring 100% compliance alignment.",
    },
    {
        id: "ecommerce",
        icon: ShoppingBag,
        title: "E-commerce & DTC Businesses",
        challenges: "Fragmented revenue streams, multi-marketplace reconciliation, and inventory tracking across time zones.",
        solutions: "Automated financial workflows that integrate Shopify, Amazon and Stripe data directly into your ledger.",
        advantage: "Real-time dashboard visibility — track true margins per SKU, not just top-line revenue.",
    },
    {
        id: "startups",
        icon: Rocket,
        title: "Startups & Scale-ups",
        challenges: "Limited internal resources, multi-skilled support needs, and maintaining quality during rapid growth.",
        solutions: "Flexible, growth-stage talent that evolves with your needs — from basic bookkeeping to complex cash-flow forecasting.",
        advantage: "Audit-ready records that build immediate credibility with your investors.",
    },
    {
        id: "consulting",
        icon: Briefcase,
        title: "Consulting Firms",
        challenges: "Administrative overhead, complex project-based billing, and time-intensive client reporting.",
        solutions: "We remove operational friction — handling CRM management, proposal preparation and project-level profitability tracking.",
        advantage: "Senior partners focus 100% on high-value, billable client work.",
    },
    {
        id: "real-estate",
        icon: Building,
        title: "Real Estate & Property",
        challenges: "Complex lease administration, tenant communication, and scattered property documentation.",
        solutions: "Centralized document management and transparent financial reporting that keeps owners and tenants on the same page.",
        advantage: "End-to-end lease compliance — you never miss a renewal or payment deadline.",
    },
    {
        id: "healthcare",
        icon: Stethoscope,
        title: "Healthcare Support Services",
        challenges: "Sensitive patient scheduling, complex billing and claims, strict regulatory documentation.",
        solutions: "HIPAA-aligned, secure non-clinical back-office support that streamlines scheduling, billing and data management.",
        advantage: "Patient-facing staff focus on care while we handle the back-end revenue cycle.",
    },
    {
        id: "it-saas",
        icon: Cpu,
        title: "IT & SaaS Companies",
        challenges: "Subscription management (MRR/ARR), deferred revenue recognition, and technical documentation.",
        solutions: "SaaS-native accounting and CRM management that tracks the KPIs your investors actually care about.",
        advantage: "We map your subscription data to recognize revenue accurately every month.",
    },
    {
        id: "logistics",
        icon: Truck,
        title: "Logistics & Supply Chain",
        challenges: "Heavy documentation processing, vendor coordination, and fragmented shipment reporting.",
        solutions: "Centralized documentation support, vendor management and live reporting dashboards.",
        advantage: "We bridge logistics vendors and your finance team — reducing shipment-to-invoice latency.",
    },
];

export default function Industries() {
    return (
        <div data-testid="industries-page">
            <Seo
                path="/industries"
                title="Industries — Bookkeeping & Back-Office for E-commerce, SaaS, Real Estate & More"
                description="Senior-led offshore bookkeeping and back-office support across Accounting, E-commerce/DTC, Startups, Consulting, Real Estate, Healthcare, SaaS and Logistics."
                keywords="ecommerce bookkeeping, SaaS accounting outsourcing, real estate bookkeeping services, healthcare back office, startup CFO services India"
            />
            <section className="container-page pt-16 md:pt-24 pb-12">
                <div className="flex items-center gap-3 mb-6">
                    <span className="ornament w-12" />
                    <p className="overline">Industries</p>
                    <span aria-hidden="true" className="eyebrow-dot" />
                </div>
                <h1 className="display text-5xl md:text-6xl lg:text-7xl text-brand-inkSoft max-w-4xl">
                    Industries we <span className="italic text-brand-amber">serve</span><span className="brand-dot">.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-brand-inkSoft/70 leading-relaxed">
                    Specialized expertise supporting international SMEs and Indian founders across
                    sectors — with the right blend of bookkeeping, advisory and back-office talent.
                </p>
            </section>

            <section className="container-page pb-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {INDUSTRIES.map((ind, i) => {
                        const Icon = ind.icon;
                        return (
                            <article
                                key={ind.id}
                                data-testid={`industry-card-${ind.id}`}
                                className="card-3d p-7 md:p-8 flex flex-col animate-fade-up"
                                style={{ animationDelay: `${i * 60}ms` }}
                            >
                                <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-creamLight text-brand-ink mb-5">
                                    <Icon size={22} strokeWidth={1.6} />
                                </span>
                                <h3 className="font-serif text-2xl text-brand-inkSoft leading-tight">{ind.title}</h3>

                                <div className="mt-5">
                                    <p className="overline text-brand-muted mb-1.5">Challenge</p>
                                    <p className="text-sm text-brand-inkSoft/80 leading-relaxed">{ind.challenges}</p>
                                </div>
                                <div className="mt-5">
                                    <p className="overline text-brand-amber mb-1.5">Our solution</p>
                                    <p className="text-sm text-brand-inkSoft/85 leading-relaxed">{ind.solutions}</p>
                                </div>
                                <div className="mt-5 pt-5 border-t border-brand-border">
                                    <p className="overline mb-1.5" style={{ color: "#E53935" }}>The Vittaniti advantage</p>
                                    <p className="text-sm text-brand-ink font-medium leading-relaxed">{ind.advantage}</p>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* Featured industries accordion teaser */}
            <IndustriesAccordion />

            {/* Don't see your industry — visually distinct call-out */}
            <section className="section container-page" data-testid="industries-cta">
                <div
                    className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-white shadow-2xl"
                    style={{
                        background:
                            "linear-gradient(135deg, #0B1B2A 0%, #142239 60%, #1E3A5F 100%)",
                    }}
                >
                    {/* decorative red accent corner */}
                    <span
                        aria-hidden="true"
                        className="absolute -top-16 -right-16 w-72 h-72 rounded-full opacity-30 blur-3xl"
                        style={{ backgroundColor: "#E53935" }}
                    />
                    <span
                        aria-hidden="true"
                        className="absolute bottom-0 left-0 w-1 h-24"
                        style={{ backgroundColor: "#E53935" }}
                    />

                    <div className="relative grid md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-8">
                            <p className="overline text-brand-amber mb-4 flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#E53935" }} />
                                Still scaling in a unique niche?
                            </p>
                            <h2 className="display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
                                We build custom teams for <span className="italic text-brand-amber">specialized</span> operations<span className="brand-dot">.</span>
                            </h2>
                            <p className="mt-6 text-white/75 leading-relaxed max-w-xl text-lg">
                                Tell us your specific workflow, and we&apos;ll design a dedicated
                                back-office solution to match.
                            </p>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-amber text-white font-semibold shadow-lg hover:scale-[1.03] transition-transform"
                                data-testid="industries-talk-cta"
                            >
                                Talk to our team <ArrowUpRight size={18} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
